import { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import css from '@styled-system/css';
import { CheckCircle } from 'react-feather';
import { BLOCKS } from '@contentful/rich-text-types';

import { contentfulPageType } from 'types';
import Layout from 'layouts';
import getContentfulImgixUrl from 'utils/getContentfulImgixUrl';
import { serverLogger } from 'utils/serverLogger';
import { getPageBySlug } from 'lib/contentful';
import { Head } from 'humanity/components/head';
import Heading from 'legacy/components/heading';
import Link from 'legacy/components/link';
import Text from 'legacy/components/text';
import Section from 'legacy/components/section';
import Box from 'legacy/components/box';
import Flex from 'legacy/components/flex';
import Image from 'legacy/components/image';
import Masthead from 'legacy/components/masthead/secondaryMasthead';
import PressCoverage from 'legacy/components/pressCoverage';
import SimpleRichText from 'legacy/components/humanity-v1/primitives/simpleRichText';

const TeamsContainer = styled(Flex)(
  css({
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    ml: [0, null, null, '-2%'],
  })
);

const RoundedImage = styled(Image)`
  img {
    box-shadow: 0px 14px 24px rgba(0, 0, 0, 0.15);
    border-radius: ${({ theme }) => theme.radii.circle}px;
  }
`;

const AvatarLink = styled(Link)(
  css({
    fontSize: 3,
    color: 'blue',
    textDecoration: 'none',
  })
);

// TODO: Once all pages using Masthead are migrated to Contentful, we can move this to that component
const mastheadRenderOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Text variant={['default', null, null, 'large']} color="white">
        {children}
      </Text>
    ),
  },
};

const bodyRenderOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Text variant={['default', null, null, 'large']}>{children}</Text>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Heading as="h3" fontSize={['2', null, '3', null, '4']}>
        {children}
      </Heading>
    ),
  },
};

const AboutUs = ({ page }) => {
  const theme = useContext(ThemeContext);

  // TODO: Once every page is migrated to Contentful, we can refactor Masthead to handle the Contentful response
  // For now, we need to convert it into a shape the component understands
  const mastheadImage = {
    url: page.masthead?.desktopImage?.image?.url,
    altText: '',
  };

  return (
    <Layout
      layout={page.layout}
      notes={page.notes?.itemsCollection?.items}
      disclosures={page.disclosures?.itemsCollection?.items}
    >
      <Head
        title={page.seoMetadata?.title}
        description={page.seoMetadata?.description}
        url={`${process.env.NEXT_PUBLIC_HI_BASE_URL}${page.seoMetadata?.canonicalUrl}`}
        noIndex={page.seoMetadata?.noIndex}
      />
      <Masthead
        eyebrow={page.masthead?.eyebrow}
        heading={page.masthead?.heading}
        image={mastheadImage}
      >
        <SimpleRichText
          content={page.masthead?.body?.json}
          renderOptions={mastheadRenderOptions}
        />
      </Masthead>
      <Section>
        <Section.Inner>
          <Box mx="auto" maxWidth="49rem" textAlign="center">
            <SimpleRichText
              content={page.sectionsCollection?.items[0]?.content?.json}
              renderOptions={bodyRenderOptions}
            />
          </Box>
        </Section.Inner>
      </Section>
      <Section mt={[-6, null, null, -7]}>
        <Section.Inner>
          <Flex flexWrap="wrap" ml={[0, null, -4]}>
            {page.sectionsCollection?.items[1]?.itemsCollection?.items?.map(
              (value, index) => (
                <Flex
                  mt={[4, null, 0]}
                  pl={[0, null, 4]}
                  width={[1, null, 1 / 3]}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                >
                  <Box mt={1}>
                    <CheckCircle size={20} color={theme.colors.teal} />
                  </Box>
                  <Box ml={2}>
                    <SimpleRichText
                      content={value.content?.json}
                      renderOptions={bodyRenderOptions}
                    />
                  </Box>
                </Flex>
              )
            )}
          </Flex>
        </Section.Inner>
      </Section>
      <PressCoverage
        heading={page.sectionsCollection?.items?.[2]?.title}
        logos={page.sectionsCollection?.items?.[2]?.itemsCollection?.items}
      />
      <Section>
        <Section.Inner>
          <Box maxWidth="55rem">
            <SimpleRichText
              content={page.sectionsCollection?.items[3]?.content?.json}
              renderOptions={bodyRenderOptions}
            />
          </Box>
          <TeamsContainer>
            {page.sectionsCollection?.items[4].itemsCollection?.items?.map((avatar) => (
              <Flex
                key={avatar.sys.id}
                flexDirection="column"
                alignItems="center"
                width={['100%', '50%', '33%', '23%']}
                mt={[5, null, 6]}
                ml={[0, null, null, '2%']}
              >
                <RoundedImage
                  src={getContentfulImgixUrl(avatar.image?.url)}
                  width={130}
                  height={130}
                  htmlAttributes={{ alt: avatar.name }}
                  additionalParams={{ fit: 'crop', ar: '1:1' }}
                  lazyload
                />
                <AvatarLink href={avatar.url} external>
                  {avatar.name}
                </AvatarLink>
                <Text color="gray" mt={0} textAlign="center">
                  {avatar.title}
                </Text>
              </Flex>
            ))}
          </TeamsContainer>
        </Section.Inner>
      </Section>
      <Section mt={[-5, null, null, -6]}>
        <Section.Inner>
          <Flex flexWrap="wrap">
            <Box width={[1, null, null, 1 / 2]} pr={[0, null, null, 3]}>
              <SimpleRichText
                content={page.sectionsCollection?.items[5]?.content?.json}
                renderOptions={bodyRenderOptions}
              />
            </Box>
            <Box
              width={[1, null, null, 1 / 2]}
              mt={[5, null, null, 0]}
              pl={[0, null, null, 3]}
            >
              <iframe
                src="https://www.google.com/maps/embed/v1/place?q=place_id:ChIJHQk724qAhYARF8R4_n9yYuw&key=AIzaSyCAqlUEfEfEgfsNwZFdHc9kaWofW5fwlDc"
                title="Human Interest Office"
                width="100%"
                height="341"
                frameBorder="0"
                allowFullScreen
              />
            </Box>
          </Flex>
        </Section.Inner>
      </Section>
    </Layout>
  );
};

AboutUs.propTypes = {
  page: contentfulPageType.isRequired,
};

export const getStaticProps = async ({ preview = false }) => {
  const SLUG = 'about-us';

  try {
    const sectionsFields = `
      ... on List {
        title
        itemsCollection(limit: 15) {
          items {
            sys {
              id
            }
            ... on TextBlock {
              content {
                json
              }
            }
            ... on Image {
              image {
                url
              }
              altText
              height
            }
            ... on Avatar {
              name
              title
              image {
                url
              }
              url
            }
          }
        }
      }
      ... on TextBlock {
        content {
          json
        }
      }
    `;

    const page = await getPageBySlug(SLUG, sectionsFields, preview);

    return { props: { page }, revalidate: 900 };
  } catch (err) {
    serverLogger.error(`Error fetching ${SLUG} page`, err, err.response?.data);
    throw new Error(`Error fetching ${SLUG} page`, { cause: err });
  }
};

export default AboutUs;
